/**
 * Lift2Fit — profile and settings.
 *
 * Fields, roles held, unit preference, an export that writes a real Blob of the
 * current dashboard JSON, and sign out. The coaching relationship — injury
 * flags, the coach link, booking, goals and chat — moved to `#/my-coach`; this
 * page keeps identity and settings only.
 */
import { useEffect, useState } from 'react';
import { api, type PlanIntake, type PolicyConsentList, type ApiResult } from '@/lib/api';
import { cn } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { Button } from '@/components/ui';
import { ArrowRightIcon, BarbellIcon, CheckIcon, GridIcon, TargetIcon, UploadIcon } from '@/lib/icons';
import { LiveMessage, Panel, PersonaBadge, ScreenHeader, StatusChip, TrackBadge } from '../components';
import { TRACKS, trackMeta } from '../demoData';
import { Link, navigate } from '../router';
import { LEGAL_DOCS, acceptedDateLabel } from '../legal/policy';

export function ProfileScreen() {
  const session = useSession();
  const { unit, setUnit } = useUnitPreference();
  const user = session.user;

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [bodyweight, setBodyweight] = useState<string>(user?.bodyweightKg === null || user?.bodyweightKg === undefined ? '' : String(user.bodyweightKg));
  const [height, setHeight] = useState<string>(user?.heightCm === null || user?.heightCm === undefined ? '' : String(user.heightCm));
  const [goal, setGoal] = useState(user?.trainingGoal ?? '');
  const [experience, setExperience] = useState<PlanIntake['experience']>(user?.experience ?? 'intermediate');
  const [weeklyTarget, setWeeklyTarget] = useState(String(user?.weeklyTarget ?? 4));
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');
  const [exporting, setExporting] = useState(false);
  // 0024 — the consent read-back. `null` means "still loading"; the panel never
  // invents a record when the API is unreachable.
  const [consents, setConsents] = useState<ApiResult<PolicyConsentList> | null>(null);

  useEffect(() => {
    let alive = true;
    void api.consents().then((result) => {
      if (alive) setConsents(result);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!user) {
    return (
      <div className="flex flex-col gap-4 border border-hairline bg-surface p-6">
        <h1 className="font-display text-2xl font-black uppercase text-ink">Not signed in</h1>
        <p className="text-[15px] text-ink-muted">Sign in to see your profile, roles and data export.</p>
        <Link to="/login" className="inline-flex w-fit h-11 items-center bg-accent px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent">
          Sign in
        </Link>
      </div>
    );
  }

  const save = () => {
    session.updateUser({
      displayName: displayName.trim() || user.displayName,
      bodyweightKg: bodyweight === '' ? null : Number(bodyweight),
      heightCm: height === '' ? null : Number(height),
      trainingGoal: goal || null,
      experience,
      weeklyTarget: Number(weeklyTarget) || user.weeklyTarget,
      unitSystem: unit,
    });
    setTone('success');
    setMessage('Profile saved to this device. The API stores it on your account when it is reachable.');
  };

  const exportData = async () => {
    setExporting(true);
    setTone('info');
    setMessage('Collecting your dashboard…');
    const [dashboard, stats] = await Promise.all([api.dashboard(session.primaryRole), api.stats()]);
    // A real account whose dashboard could not be reached has no demo numbers to
    // export: say so instead of writing a file full of nulls.
    if (dashboard.data === null || stats.data === null) {
      setExporting(false);
      setTone('danger');
      setMessage('Could not reach the API for your dashboard — nothing was exported. Try again when it is reachable.');
      return;
    }
    const payload = {
      exportedAt: new Date().toISOString(),
      source: dashboard.source,
      user,
      dashboard: dashboard.data,
      stats: stats.data,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `lift2fit-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setExporting(false);
    setTone('success');
    setMessage(`Exported ${(blob.size / 1024).toFixed(1)} kB of your data as JSON.`);
  };

  const meta = trackMeta(user.track);
  // The read-back payload (record list + the current version), once it lands.
  const consentList = consents?.data ?? null;

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        eyebrow="Account"
        title="Profile & settings"
        badge={<TrackBadge track={user.track} />}
        lead={`${meta.persona} · ${user.tier} tier · ${user.timezone}`}
        actions={<PersonaBadge track={user.track} />}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Panel title="Profile" subtitle="These feed the coach's intake."
          actions={
            <Button size="sm" onClick={save}>
              Save changes
            </Button>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Display name</span>
              <input
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Email</span>
              <input
                type="email"
                value={user.email}
                readOnly
                className="h-11 border border-hairline bg-page-subtle px-3 text-[15px] text-ink-muted"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Bodyweight (kg)</span>
              <input
                type="number"
                inputMode="decimal"
                value={bodyweight}
                onChange={(event) => setBodyweight(event.target.value)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Height (cm)</span>
              <input
                type="number"
                inputMode="decimal"
                value={height}
                onChange={(event) => setHeight(event.target.value)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Training goal</span>
              <input
                type="text"
                value={goal}
                onChange={(event) => setGoal(event.target.value)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Experience</span>
              <select
                value={experience}
                onChange={(event) => setExperience(event.target.value as PlanIntake['experience'])}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              >
                {(['beginner', 'intermediate', 'advanced'] as const).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Weekly session target</span>
              <input
                type="number"
                min={1}
                max={7}
                value={weeklyTarget}
                onChange={(event) => setWeeklyTarget(event.target.value)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Training track</span>
              <select
                value={user.track}
                onChange={(event) => session.switchTrack(event.target.value as typeof user.track)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              >
                {TRACKS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <LiveMessage message={message} tone={tone} />
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel title="Your training" subtitle="Your own log and your own programme — both stay inside the app.">
            <div className="flex flex-col gap-2">
              <Link
                to="/grid"
                data-testid="profile-grid-link"
                className="flex items-center justify-between gap-3 border border-hairline px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent"
              >
                <span className="flex items-center gap-2">
                  <GridIcon className="text-[15px] text-accent" />
                  My Grid
                </span>
                <ArrowRightIcon />
              </Link>
              <Link
                to="/programme"
                data-testid="profile-programme-link"
                className="flex items-center justify-between gap-3 border border-hairline px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent"
              >
                <span className="flex items-center gap-2">
                  <BarbellIcon className="text-[15px] text-accent" />
                  My Programme
                </span>
                <ArrowRightIcon />
              </Link>
            </div>
            <p className="text-[12px] text-ink-muted">
              The Grid logs your sets live; the Programme is the block those sessions come from.
            </p>
            {session.hasRole('coach') ? (
              <p
                data-testid="profile-coach-lifter-promo"
                className="border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[12px] text-ink"
              >
                <span className="font-bold uppercase tracking-[0.06em]">Included with your workspace:</span>{' '}
                a coaching workspace, and a free lifter account for your own training. Your clients’ training
                lives in the workspace; this lifter account is yours to log in.
              </p>
            ) : null}
          </Panel>

          <Panel title="Roles held" subtitle="Everyone is a lifter first; the coach role is added to the same account.">
            <ul className="flex flex-col gap-3">
              {(['lifter', 'coach'] as const).map((role) => {
                const held = session.hasRole(role);
                return (
                  <li key={role} className="flex items-start justify-between gap-3 border-b border-hairline pb-3 last:border-b-0">
                    <span className="flex flex-col">
                      <span className="text-[14px] font-bold uppercase tracking-[0.06em] text-ink">{role}</span>
                      <span className="text-[13px] text-ink-muted">
                        {role === 'lifter'
                          ? 'The Grid, dashboard, streaks and generated plans.'
                          : 'A coaching workspace: every client linked to you, their programmes and your roster.'}
                      </span>
                    </span>
                    {held ? (
                      <StatusChip tone="success" icon={<CheckIcon />}>
                        Held
                      </StatusChip>
                    ) : (
                      <StatusChip tone="neutral">Not held</StatusChip>
                    )}
                  </li>
                );
              })}
            </ul>
          </Panel>

          <Panel title="Units" subtitle="Canonical storage is kilograms; this changes display only.">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="unit-metric"
                aria-pressed={unit === 'metric'}
                onClick={() => setUnit('metric')}
                className={cn(
                  'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                  unit === 'metric' ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
                )}
              >
                Kilograms
              </button>
              <button
                type="button"
                data-testid="unit-imperial"
                aria-pressed={unit === 'imperial'}
                onClick={() => setUnit('imperial')}
                className={cn(
                  'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                  unit === 'imperial' ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
                )}
              >
                Pounds
              </button>
            </div>
          </Panel>

          <Panel
            title="Coaching"
            subtitle="Your coach, your injury flags, your goals and your chat now live on your own Manage coach page."
          >
            <ul className="flex flex-col gap-2 text-[13px] text-ink-muted">
              <li className="flex items-center gap-2">
                <CheckIcon className="text-success" />
                Sharing: the sessions and sets a linked coach can see, plus any other fields you enable.
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-[2px] text-success" />
                <span>
                  A linked coach can open the sessions you share. A session shows the pain scores and notes you
                  logged only when you also enable those fields — they stay off until you turn them on. Review
                  what you are sharing, or revoke the link, from Manage coach.
                </span>
              </li>
            </ul>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/my-coach"
                data-testid="profile-manage-coach"
                className="inline-flex w-fit h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
              >
                Manage coach
                <ArrowRightIcon />
              </Link>
              {session.hasRole('coach') ? (
                <Link
                  to="/crm/clients"
                  className="inline-flex w-fit h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                >
                  Open the coaching workspace
                  <ArrowRightIcon />
                </Link>
              ) : null}
            </div>
            <p className="text-[13px] text-ink-muted">
              Injury flags, the “your coach” link, booking, goals and chat all moved there — this page keeps your
              identity, units, export and session.
            </p>
          </Panel>

          <Panel title="Your data" subtitle="Export the dashboard and stats the API holds for you.">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={exportData} disabled={exporting}>
                <UploadIcon className="rotate-180" />
                {exporting ? 'Preparing…' : 'Export my data (JSON)'}
              </Button>
              <span className="text-[12px] text-ink-muted">Real Blob download of the current dashboard payload.</span>
            </div>
          </Panel>

          <Panel
            title="Policy acceptance"
            subtitle="The record created when you ticked “I have read and understand”. Your right of access means you can see it."
          >
            {consents === null ? (
              <p className="text-[13px] text-ink-muted">Loading your acceptance record…</p>
            ) : consents.problem ? (
              <p data-testid="profile-policy-error" className="text-[13px] text-danger">
                Could not load your acceptance record: {consents.problem.detail ?? consents.problem.title}.
              </p>
            ) : !consentList || consentList.data.length === 0 ? (
              <p data-testid="profile-policy-empty" className="text-[13px] text-ink-muted">
                {session.isDemo
                  ? 'Demo session — no server-side acceptance record exists.'
                  : 'No acceptance is recorded on the server for this account.'}
              </p>
            ) : (
              <ul data-testid="profile-policy-record" className="flex flex-col gap-3">
                {consentList.data.map((row) => (
                  <li
                    key={row.id}
                    className="border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[13px] text-ink"
                  >
                    You accepted the Privacy Policy, Terms of Service and Health Disclaimer — policy version{' '}
                    <span className="font-bold">{row.policyVersion}</span> — on{' '}
                    <span className="font-bold">{acceptedDateLabel(row.acceptedAt)}</span>.
                    {consentList.currentVersion && row.policyVersion !== consentList.currentVersion ? (
                      <span className="mt-1 block text-ink-muted">
                        The current version is {consentList.currentVersion}. This account has not accepted it.
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[12px] text-ink-muted">
              Read the current documents:{' '}
              <Link
                to={LEGAL_DOCS.health_disclaimer.path}
                className="font-bold text-accent hover:underline"
              >
                Health Disclaimer
              </Link>
              {' · '}
              <Link to={LEGAL_DOCS.privacy_policy.path} className="font-bold text-accent hover:underline">
                Privacy Policy
              </Link>
              {' · '}
              <Link to={LEGAL_DOCS.terms_of_service.path} className="font-bold text-accent hover:underline">
                Terms
              </Link>
            </p>
          </Panel>

          <Panel title="Session" subtitle={`Signed in as ${user.email}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  session.logout();
                  navigate('/login', { replace: true });
                }}
              >
                Sign out
              </Button>
              <span className="text-[12px] text-ink-muted">
                {session.isDemo ? 'Demo session — nothing is stored on a server.' : 'Tokens are cleared from this device.'}
              </span>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
